<template>
  <div class="prediction-view">
    <div class="prediction-header">
      <button
        v-if="showBackToFarm"
        type="button"
        class="prediction-back"
        @click="$emit('back-to-farm')"
      >
        ← Resumen de finca
      </button>
      <h2 class="prediction-title">Predicción de zona</h2>
      <p v-if="zoneLabel" class="prediction-subtitle">{{ zoneLabel }}</p>
    </div>

    <div v-if="loading" class="prediction-state">Cargando predicciones…</div>
    <div v-else-if="error" class="prediction-state prediction-state--error">{{ error }}</div>
    <div v-else-if="!displayPredictions.length" class="prediction-state">
      No hay predicciones disponibles para esta zona.
    </div>

    <template v-else>
      <div class="prediction-kpis">
        <div class="kpi-card">
          <span class="kpi-label">Acumulado proyectado ({{ displayPredictions.length }} días)</span>
          <span class="kpi-value">{{ formatKg(totalProjectedKg) }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">Día de mayor incremento</span>
          <span class="kpi-value">{{ peakDayLabel }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">Score medio</span>
          <span class="kpi-value">{{ averageScore }}</span>
        </div>
      </div>

      <div class="prediction-legend">
        <span class="legend-item"><i class="legend-swatch legend-swatch--high"></i> Incremento alto</span>
        <span class="legend-item"><i class="legend-swatch legend-swatch--moderate"></i> Moderado</span>
        <span class="legend-item"><i class="legend-swatch legend-swatch--low"></i> Bajo</span>
        <span class="legend-note">Borde punteado = score bajo</span>
      </div>
      <p class="prediction-hint">Cada celda muestra el kg que se suma ese día (no el acumulado).</p>

      <div class="prediction-grid">
        <div
          v-for="day in chartDays"
          :key="day.id"
          class="day-card"
          :class="[
            `day-card--${day.level}`,
            { 'day-card--low-score': day.lowScore },
          ]"
        >
          <span class="day-card__date">{{ day.dateLabel }}</span>
          <div class="day-card__bar" :class="`day-card__bar--${day.level}`"></div>
          <span class="day-card__weight">+{{ formatKg(day.kg) }}</span>
          <span class="day-card__score">score {{ day.score }}</span>
        </div>
      </div>

      <div class="prediction-chart">
        <h3 class="chart-title">Kg acumulado total</h3>
        <svg
          class="chart-svg"
          :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
          preserveAspectRatio="xMidYMid meet"
        >
          <line
            v-for="(tick, i) in yTicks"
            :key="'grid-' + i"
            :x1="chartPadding.left"
            :y1="tick.y"
            :x2="chartWidth - chartPadding.right"
            :y2="tick.y"
            class="chart-grid-line"
          />
          <polyline :points="linePoints" class="chart-line" fill="none" />
          <circle
            v-for="(point, i) in chartPoints"
            :key="'point-' + i"
            :cx="point.x"
            :cy="point.y"
            r="5"
            class="chart-dot"
          />
          <text
            v-for="(point, i) in chartPoints"
            :key="'label-x-' + i"
            :x="point.x"
            :y="chartHeight - 6"
            class="chart-axis-label"
            text-anchor="middle"
          >{{ point.dateLabel }}</text>
          <text
            v-for="(tick, i) in yTicks"
            :key="'label-y-' + i"
            :x="chartPadding.left - 8"
            :y="tick.y + 4"
            class="chart-axis-label"
            text-anchor="end"
          >{{ tick.label }}</text>
        </svg>
      </div>
    </template>
  </div>
</template>

<script>
import {
  buildMockZonePredictions,
  filterForwardPredictions,
} from '../../utils/predictionHorizon';

const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// TODO: eliminar cuando la integración con el modelo esté activa
const USE_MOCK_FALLBACK = true;

export default {
  name: 'PredictionsWidget',

  props: {
    zona: {
      type: Object,
      default: null,
    },
    finca: {
      type: Object,
      default: null,
    },
    showBackToFarm: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      predictions: [],
      loading: false,
      error: null,
      chartWidth: 360,
      chartHeight: 200,
      chartPadding: { top: 16, right: 12, bottom: 28, left: 40 },
    };
  },

  computed: {
    displayPredictions() {
      const forward = filterForwardPredictions(this.predictions);
      if (forward.length) return forward;
      if (USE_MOCK_FALLBACK && this.zona) return buildMockZonePredictions();
      return [];
    },

    zoneLabel() {
      if (!this.zona) return '';
      const farm = this.finca?.name ? `${this.finca.name} · ` : '';
      return `${farm}${this.zona.name || this.zona.code}`;
    },

    chartDays() {
      const weights = this.displayPredictions.map((p) => this.toKg(p.net_weight));
      const maxWeight = Math.max(...weights, 1);

      return this.displayPredictions.map((p) => {
        const kg = this.toKg(p.net_weight);
        const ratio = kg / maxWeight;
        let level = 'low';
        if (ratio >= 0.66) level = 'high';
        else if (ratio >= 0.33) level = 'moderate';

        return {
          id: p.id,
          dateLabel: this.formatShortDate(p.prediction_date),
          kg,
          score: p.score,
          level,
          lowScore: p.score <= 5,
        };
      });
    },

    totalProjectedKg() {
      return this.displayPredictions.reduce((sum, p) => sum + this.toKg(p.net_weight), 0);
    },

    averageScore() {
      if (!this.displayPredictions.length) return '—';
      const total = this.displayPredictions.reduce((sum, p) => sum + (p.score || 0), 0);
      const avg = total / this.displayPredictions.length;
      return Number.isInteger(avg) ? String(avg) : avg.toFixed(1).replace('.', ',');
    },

    peakDayLabel() {
      if (!this.displayPredictions.length) return '—';
      const peak = [...this.displayPredictions].sort(
        (a, b) => this.toKg(b.net_weight) - this.toKg(a.net_weight),
      )[0];
      return this.formatShortDate(peak.prediction_date);
    },

    chartPoints() {
      const plotW = this.chartWidth - this.chartPadding.left - this.chartPadding.right;
      const plotH = this.chartHeight - this.chartPadding.top - this.chartPadding.bottom;
      const maxCumulative = Math.max(...this.cumulativeValues, 1);
      let cumulative = 0;

      return this.displayPredictions.map((p, i) => {
        cumulative += this.toKg(p.net_weight);
        const x =
          this.chartPadding.left +
          (this.displayPredictions.length === 1
            ? plotW / 2
            : (i / (this.displayPredictions.length - 1)) * plotW);
        const y =
          this.chartPadding.top + plotH - (cumulative / maxCumulative) * plotH;
        return {
          x,
          y,
          dateLabel: this.formatShortDate(p.prediction_date),
          cumulative,
        };
      });
    },

    cumulativeValues() {
      let cumulative = 0;
      return this.displayPredictions.map((p) => {
        cumulative += this.toKg(p.net_weight);
        return cumulative;
      });
    },

    linePoints() {
      return this.chartPoints.map((p) => `${p.x},${p.y}`).join(' ');
    },

    yTicks() {
      const plotH = this.chartHeight - this.chartPadding.top - this.chartPadding.bottom;
      const maxVal = Math.max(...this.cumulativeValues, 1);
      const steps = 4;
      const ticks = [];

      for (let i = 0; i <= steps; i++) {
        const value = (maxVal / steps) * i;
        const y = this.chartPadding.top + plotH - (value / maxVal) * plotH;
        ticks.push({
          y,
          label: `${Math.round(value)}`,
        });
      }
      return ticks;
    },
  },

  watch: {
    zona: {
      immediate: true,
      handler() {
        this.loadPredictions();
      },
    },
  },

  methods: {
    async loadPredictions() {
      if (!this.zona) {
        this.predictions = [];
        return;
      }

      this.loading = true;
      this.error = null;

      if (!this.zona.id) {
        this.predictions = [];
        this.loading = false;
        return;
      }

      try {
        const res = await this.$http.get(
          `${this.$hostname}/zones/${this.zona.id}/predicciones/`,
        );
        const data = Array.isArray(res.data) ? res.data : [];
        this.predictions = data.length ? data : [];
      } catch (err) {
        if (err?.response?.status === 403) {
          this.error = 'No tienes permisos para ver predicciones.';
          this.predictions = [];
        } else if (!USE_MOCK_FALLBACK) {
          this.error = 'No se pudieron cargar las predicciones.';
          this.predictions = [];
        } else {
          this.predictions = [];
        }
        console.error('Error cargando predicciones:', err);
      } finally {
        this.loading = false;
      }
    },

    toKg(grams) {
      return Math.round((Number(grams) || 0) / 10) / 100;
    },

    formatKg(kg) {
      const value = Number(kg) || 0;
      return `${value.toLocaleString('es-ES', { maximumFractionDigits: 0 })} kg`;
    },

    formatShortDate(dateStr) {
      if (!dateStr) return '—';
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '—';
      return `${date.getDate()} ${MONTHS_ES[date.getMonth()]}`;
    },
  },
};
</script>

<style scoped>
.prediction-view {
  padding: 12px 14px 24px;
  max-width: 900px;
  margin: 0 auto;
  color: #1a1a1a;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.prediction-header {
  margin-bottom: 16px;
}

.prediction-back {
  display: inline-block;
  margin-bottom: 8px;
  padding: 0;
  border: none;
  background: none;
  color: #107631;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.prediction-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #107631;
}

.prediction-subtitle {
  margin: 4px 0 0;
  font-size: 0.9rem;
  color: #444;
}

.prediction-state {
  text-align: center;
  padding: 40px 16px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  color: #555;
}

.prediction-state--error {
  color: #b45309;
}

.prediction-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 14px;
}

.kpi-card {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 10px;
  padding: 10px 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.kpi-label {
  font-size: 0.65rem;
  color: #666;
  line-height: 1.2;
}

.kpi-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #107631;
}

.prediction-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  margin-bottom: 6px;
  font-size: 0.75rem;
  color: #444;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.legend-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.legend-swatch--high { background: #107631; }
.legend-swatch--moderate { background: #d4a017; }
.legend-swatch--low { background: #c0392b; }

.legend-note {
  font-style: italic;
  color: #777;
  font-size: 0.7rem;
}

.prediction-hint {
  margin: 0 0 12px;
  font-size: 0.72rem;
  color: #666;
}

.prediction-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 18px;
}

.day-card {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 10px;
  padding: 8px 6px;
  text-align: center;
  border: 2px solid transparent;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-height: 88px;
}

.day-card--low-score {
  border-style: dashed;
  border-color: rgba(0, 0, 0, 0.25);
}

.day-card--high { background: rgba(16, 118, 49, 0.1); }
.day-card--moderate { background: rgba(212, 160, 23, 0.12); }
.day-card--low { background: rgba(192, 57, 43, 0.1); }

.day-card__date {
  font-size: 0.72rem;
  font-weight: 600;
  color: #333;
}

.day-card__bar {
  width: 70%;
  height: 5px;
  border-radius: 3px;
}

.day-card__bar--high { background: #107631; }
.day-card__bar--moderate { background: #d4a017; }
.day-card__bar--low { background: #c0392b; }

.day-card__weight {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1a1a1a;
}

.day-card__score {
  font-size: 0.65rem;
  color: #666;
}

.prediction-chart {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 12px;
  padding: 12px 8px 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.chart-title {
  margin: 0 0 8px 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #107631;
}

.chart-svg {
  width: 100%;
  height: auto;
  display: block;
}

.chart-grid-line {
  stroke: rgba(0, 0, 0, 0.08);
  stroke-width: 1;
}

.chart-line {
  stroke: #107631;
  stroke-width: 2.5;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.chart-dot {
  fill: #107631;
  stroke: #fff;
  stroke-width: 2;
}

.chart-axis-label {
  font-size: 9px;
  fill: #666;
}

@media (max-width: 600px) {
  .prediction-kpis {
    grid-template-columns: 1fr;
  }

  .prediction-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .kpi-value {
    font-size: 1.1rem;
  }
}
</style>
